<template>
  <Dialog v-model:visible="visible" header="Image Workflow" :style="{ width: '40rem' }" modal>
    <div v-if="loading" class="text-surface-400 text-sm py-4">Loading...</div>
    <div v-else-if="workflow">
      <div v-if="hasNoPlans" class="text-surface-500 text-sm py-4">No tasks for this image.</div>
      <div v-for="version in workflow.versions" :key="version.id" class="mb-4">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-sm font-medium text-surface-200">
            {{ version.original ? 'Original' : 'Output' }}
          </span>
          <span v-if="version.created" class="text-xs text-surface-500">
            {{ formatDate(version.created) }}
          </span>
        </div>
        <div v-if="version.plans.length > 0">
          <TaskPlanCard
            v-for="plan in version.plans"
            :key="plan.id"
            :plan="plan"
            @rerun-complete="fetchWorkflow"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import Dialog from 'primevue/dialog'
import dayjs from 'dayjs'
import type { ImageWorkflowDto } from '@/api/generated'
import { useApiClient } from '@/api/apiClient'
import TaskPlanCard from './TaskPlanCard.vue'

const props = defineProps<{
  imageId: string
}>()

const visible = defineModel<boolean>('visible', { default: false })

const { api } = useApiClient()

const loading = ref(false)
const workflow = ref<ImageWorkflowDto | null>(null)

const hasNoPlans = computed(
  () => workflow.value && workflow.value.versions.every(v => v.plans.length === 0),
)

async function fetchWorkflow() {
  loading.value = true
  try {
    const { data } = await api.taskPlanControllerGetImageWorkflow({
      path: { imageId: props.imageId },
    })
    workflow.value = data ?? null
  } finally {
    loading.value = false
  }
}

watch(visible, newVal => {
  if (newVal) {
    fetchWorkflow()
  }
})

function formatDate(epoch: number): string {
  return dayjs(epoch).format('YYYY-MM-DD HH:mm')
}
</script>
